<template lang="pug">
  .camera-capture
    //- Estado: error de permisos
    camera-permission(
      v-if="errorName"
      :error-name="errorName"
      @retry="startCamera"
    )

    //- Estado: cargando cámara
    .camera-loading(v-else-if="loading")
      b-spinner(variant="light" label="Cargando cámara...")
      p.loading-text Iniciando cámara...

    //- Vista de la cámara en vivo
    .camera-live(v-show="!errorName && !loading")
      canvas.camera-canvas(ref="cameraCanvas")
      .camera-controls
        button.capture-btn(@click="takePhoto" title="Tomar foto")
          span.material-icons photo_camera

    //- Canvas oculto para capturar la foto en formato vertical
    canvas.hidden-canvas(ref="captureCanvas")

    //- Overlay de previsualización de la foto
    transition(name="fade")
      .photo-preview-overlay(v-if="previewVisible")
        .preview-container
          .preview-header
            span.preview-title ¿La imagen se ve clara?
          .preview-image-wrapper
            img.preview-image(:src="capturedPhoto" alt="Foto capturada")
          .preview-actions
            button.action-btn.reject-btn(@click="rejectPhoto")
              span.material-icons refresh
              span.action-label Repetir
            button.action-btn.accept-btn(@click="acceptPhoto")
              span.material-icons check_circle
              span.action-label Aceptar
</template>

<script>
import CameraPermission from '../common/CameraPermission.vue'
import * as Sentry from '@sentry/vue'

export default {
  name: 'CameraCapture',
  components: { CameraPermission },
  props: {
    cameraId: {
      type: String,
      default: null
    },
    facingMode: {
      type: String,
      default: 'environment' // 'user' para cámara frontal, 'environment' para trasera
    },
    outputWidth: {
      type: Number,
      default: 720
    },
    outputHeight: {
      type: Number,
      default: 1280
    },
    quality: {
      type: Number,
      default: 0.85
    }
  },
  data() {
    return {
      stream: null,
      loading: false,
      errorName: '',
      capturedPhoto: null,
      previewVisible: false,
      animationFrameId: null
    }
  },
  mounted() {
    this.startCamera()
  },
  watch: {
    async cameraId(newId, oldId) {
      if (newId !== oldId) {
        await this.stopCamera()
        this.startCamera()
      }
    }
  },
  beforeDestroy() {
    this.stopCamera()
  },
  methods: {
    async startCamera() {
      // Abort guard: if another startCamera call comes in while this one is
      // still awaiting getUserMedia / play, the old call detects the mismatch
      // and exits early, preventing two concurrent streams.
      const startId = (this._startId = (this._startId || 0) + 1)

      this.loading = true
      this.errorName = ''

      try {
        let stream

        if (this.cameraId) {
          // 'exact' forces the specific selected camera.
          // Safe now that we no longer combine it with resolution or facingMode constraints.
          stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: this.cameraId } },
            audio: false
          })
        } else {
          // No device selected: prefer environment camera, fall back to any camera.
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { ideal: this.facingMode } },
              audio: false
            })
          } catch {
            stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
          }
        }

        this.stream = stream

        // If a newer startCamera call came in while we were awaiting getUserMedia, abort.
        if (startId !== this._startId) {
          this.stream.getTracks().forEach(t => t.stop())
          return
        }

        // Crear un video temporal para alimentar el canvas
        this._video = document.createElement('video')
        this._video.srcObject = this.stream
        this._video.setAttribute('playsinline', 'true')
        this._video.setAttribute('autoplay', 'true')
        this._video.muted = true

        await this._video.play()

        await new Promise((resolve) => {
          if (this._video.readyState >= 2) {
            resolve()
          } else {
            this._video.onloadedmetadata = () => resolve()
          }
        })

        // If superseded while waiting for metadata, abort.
        if (startId !== this._startId) return

        // Configurar el canvas de previsualización
        this.$nextTick(() => {
          this._drawFrame()
        })
      } catch (error) {
        console.error('Error al acceder a la cámara:', error)
        this.errorName = error.name || 'UnknownError'
        Sentry.captureException(error, {
          tags: { context: 'CameraCapture.startCamera', errorName: this.errorName },
          extra: { cameraId: this.cameraId, facingMode: this.facingMode }
        })
      } finally {
        this.loading = false
      }
    },

    _drawFrame() {
      if (!this._video || !this.$refs.cameraCanvas) return

      const canvas = this.$refs.cameraCanvas
      const ctx = canvas.getContext('2d')
      const video = this._video

      if (!video.videoWidth) {
        this.animationFrameId = requestAnimationFrame(() => this._drawFrame())
        return
      }

      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      this.animationFrameId = requestAnimationFrame(() => this._drawFrame())
    },

    takePhoto() {
      if (!this._video || this.previewVisible) return

      const canvas = this.$refs.cameraCanvas
      if (!canvas) return

      const captureCanvas = this.$refs.captureCanvas
      const ctx = captureCanvas.getContext('2d')
      const video = this._video

      const vWidth = video.videoWidth
      const vHeight = video.videoHeight
      if (!vWidth || !vHeight) return

      const clientWidth = canvas.clientWidth
      const clientHeight = canvas.clientHeight

      // Determinar la relación de aspecto visual de la cámara (lo que el usuario ve en pantalla)
      const R_dom = (clientWidth && clientHeight) ? (clientWidth / clientHeight) : (this.outputWidth / this.outputHeight)
      const videoRatio = vWidth / vHeight

      let drawWidth = vWidth
      let drawHeight = vHeight
      let startX = 0
      let startY = 0

      if (videoRatio > R_dom) {
        // El video es más ancho que la proporción visual del contenedor (recortar lados)
        drawWidth = vHeight * R_dom
        startX = (vWidth - drawWidth) / 2
      } else {
        // El video es más alto que la proporción visual del contenedor (recortar arriba/abajo)
        drawHeight = vWidth / R_dom
        startY = (vHeight - drawHeight) / 2
      }

      // Ajustar el tamaño del canvas de captura para que coincida con la proporción visual
      let outWidth = this.outputWidth
      let outHeight = this.outputHeight

      if (R_dom < 1) {
        // En orientación vertical, priorizamos el alto de salida deseado
        outHeight = this.outputHeight
        outWidth = Math.round(outHeight * R_dom)
      } else {
        // En orientación horizontal, priorizamos el ancho de salida deseado
        outWidth = this.outputWidth
        outHeight = Math.round(outWidth / R_dom)
      }

      captureCanvas.width = outWidth
      captureCanvas.height = outHeight

      // Dibujar el frame actual del video con el recorte correspondiente a lo que ve el usuario
      ctx.drawImage(video, startX, startY, drawWidth, drawHeight, 0, 0, outWidth, outHeight)

      // Obtener la imagen optimizada como JPEG
      this.capturedPhoto = captureCanvas.toDataURL('image/jpeg', this.quality)
      this.previewVisible = true
    },

    async acceptPhoto() {
      this.$emit('photo-accepted', await this.getPhotoBlob())
      this._closePreview()
    },

    rejectPhoto() {
      this.$emit('photo-rejected')
      this._closePreview()
    },

    _closePreview() {
      this.previewVisible = false
      this.capturedPhoto = null
    },

    stopCamera() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
        this.animationFrameId = null
      }

      if (this._video) {
        this._video.pause()
        this._video.srcObject = null
        this._video = null
      }

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
    },

    /**
     * Retorna la foto capturada como un Blob para subir al servidor.
     * @returns {Promise<Blob|null>}
     */
    async getPhotoBlob() {
      if (!this.capturedPhoto) return null

      const response = await fetch(this.capturedPhoto)
      return response.blob()
    }
  }
}
</script>

<style scoped>
.camera-capture {
  position: relative;
  width: 100%;
  background: #000;
  overflow: hidden;
}

/* ---------- Estados ---------- */
.camera-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 2rem;
  text-align: center;
}

.loading-text {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 1rem;
  font-size: 0.9rem;
}

/* ---------- Cámara en vivo ---------- */
.camera-live {
  position: relative;
  width: 100%;
  height: 100%;
}

.camera-canvas {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-controls {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
}

.capture-btn {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 4px solid var(--primary);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: white;
}


.capture-btn .material-icons {
  font-size: 28px;
}

/* ---------- Canvas oculto ---------- */
.hidden-canvas {
  display: none;
}

/* ---------- Overlay de previsualización ---------- */
.photo-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.preview-container {
  display: flex;
  flex-direction: column;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
}

.preview-header {
  text-align: center;
  padding: 0.75rem 1rem;
  background: rgba(30, 30, 30, 0.9);
  border-radius: 12px 12px 0 0;
  backdrop-filter: blur(10px);
}

.preview-title {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.preview-image-wrapper {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
}

.preview-image {
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
}

.preview-actions {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(30, 30, 30, 0.9);
  border-radius: 0 0 12px 12px;
  backdrop-filter: blur(10px);
  justify-content: center;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.5rem;
  border: none;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
  color: #fff;
}

.action-btn:active {
  transform: scale(0.95);
}

.action-btn .material-icons {
  font-size: 22px;
}

.action-label {
  pointer-events: none;
}

.reject-btn {
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  box-shadow: 0 4px 14px rgba(231, 76, 60, 0.35);
}

.reject-btn:hover {
  box-shadow: 0 6px 20px rgba(231, 76, 60, 0.5);
  transform: scale(1.03);
}

.accept-btn {
  background: linear-gradient(135deg, #27ae60, #1e8449);
  box-shadow: 0 4px 14px rgba(39, 174, 96, 0.35);
}

.accept-btn:hover {
  box-shadow: 0 6px 20px rgba(39, 174, 96, 0.5);
  transform: scale(1.03);
}

/* ---------- Transición ---------- */
.fade-enter-active,
.fade-leave-active {}

.fade-enter,
.fade-leave-to {}
</style>
